import type { TransientFileRegistry } from './filePicker';
import type {
  CapabilityManifest,
  DeclarativeCapabilityAuthoringRegistration,
  DesignCapabilityCatalogArtifact,
  JsonObject,
  JsonValue,
  ProductContext,
  RenderNode,
  ViewProviderDescriptor,
} from "@inf-monkeys-tech/monkeys";
import type { RenderTree } from "@inf-monkeys-tech/monkeys";
import type { ReactNode } from "react";

import type { RenderTreeNodeContext } from "../runtime";

export type CapabilityCatalogCategory =
  | "layout"
  | "navigation"
  | "data-view"
  | "data-editor"
  | "content"
  | "status"
  | "professional";

export interface CapabilityOntologyCompatibility {
  inputPort: string;
  schemaRef: string;
  mode: "view" | "filter";
}

export interface CapabilityEditorMetadata {
  eligible: boolean;
  category: CapabilityCatalogCategory;
  labelKey: string;
  descriptionKey: string;
  slots: readonly string[];
  ontologyCompatibility: readonly CapabilityOntologyCompatibility[];
}

export interface DesignCapabilityCatalogEntry {
  manifest: CapabilityManifest;
  provider: ViewProviderDescriptor;
  editor: CapabilityEditorMetadata;
  declarativeRegistration: DeclarativeCapabilityAuthoringRegistration;
}

export interface CompiledDesignCapabilityCatalog {
  entries: readonly DesignCapabilityCatalogEntry[];
  manifests: readonly CapabilityManifest[];
  providers: readonly ViewProviderDescriptor[];
  entriesByProviderId: ReadonlyMap<string, DesignCapabilityCatalogEntry>;
  entriesByCapabilityId: ReadonlyMap<
    string,
    readonly DesignCapabilityCatalogEntry[]
  >;
  requireProvider(providerId: string): DesignCapabilityCatalogEntry;
  resolve(
    capabilityId: string,
    product: ProductContext,
    preferredProviderId?: string,
  ): DesignCapabilityCatalogEntry;
}

export interface CompileDesignCapabilityCatalogArtifactInput {
  catalog: CompiledDesignCapabilityCatalog;
  sourceRef?: string;
  revision?: number;
}

export type CompiledDesignCapabilityCatalogArtifact =
  DesignCapabilityCatalogArtifact;

export type CapabilityTranslateValues = Readonly<
  Record<string, string | number | boolean>
>;

export type CapabilityTranslate = (
  key: string,
  values?: CapabilityTranslateValues,
) => string;

export interface CapabilityAccessDecision {
  allowed: boolean;
  reasonCode?: string;
}

export interface CapabilityIntent {
  type: string;
  payload: JsonObject;
}

export interface DesignCapabilityPendingActionInvocation {
  invocationKey: string;
  actionBindingId: string;
  source: {
    capabilityInstanceId: string;
    port: string;
  };
  intent: JsonObject;
}

export type GalleryCapabilityItemModel = JsonObject & {
  id: string;
  title?: string;
  titleKey?: string;
  description?: string;
  descriptionKey?: string;
  actionLabelKey?: string;
  media?: GalleryCapabilityMediaModel;
};

export type GalleryCapabilityMediaReferenceModel = JsonObject & {
  id: string;
};

export type GalleryCapabilityMediaModel = JsonObject & {
  reference: GalleryCapabilityMediaReferenceModel;
  alt: string;
  aspectRatio?: "1:1" | "4:3" | "3:4" | "16:9" | "9:16";
  placeholder?: "neutral" | "checkerboard";
};

export type ResolvedGalleryCapabilityMedia = {
  src: string;
};

export type GalleryCapabilityPaginationModel = JsonObject & {
  hasMore: boolean;
  nextCursor: string | null;
  loadingMore: boolean;
  errorCode?: string;
};

export type GalleryCapabilityRenderModel = JsonObject & {
  items: GalleryCapabilityItemModel[];
  pagination: GalleryCapabilityPaginationModel;
};

export type GalleryLoadMoreIntentPayload = JsonObject & {
  cursor: string;
};

export type CapabilityIntentHandler = (
  intent: CapabilityIntent,
  node: RenderNode,
) => void;

export interface CapabilityProviderLoadContext {
  node: RenderNode;
  manifest: CapabilityManifest;
  descriptor: ViewProviderDescriptor;
  binding: unknown;
  signal: AbortSignal;
}

export interface CapabilityProviderResult {
  model: JsonObject;
  empty?: boolean;
}

export interface DesignCapabilityProvider {
  providerId: string;
  sideEffectAdapterId?: string;
  load?: (
    context: CapabilityProviderLoadContext,
  ) => CapabilityProviderResult | Promise<CapabilityProviderResult>;
}

export type DesignCapabilityProviderRegistry = Readonly<
  Record<string, DesignCapabilityProvider>
>;

export type CapabilityPerformanceOutcome =
  | "success"
  | "empty"
  | "error"
  | "cancelled"
  | "budget-exceeded";

export interface CapabilityPerformanceEvidence {
  capabilityId: string;
  providerId: string;
  nodeId: string;
  durationMs: number;
  budgetMs?: number;
  outcome: CapabilityPerformanceOutcome;
}

export type CapabilityPerformanceEvidenceHandler = (
  evidence: CapabilityPerformanceEvidence,
) => void;

export interface DesignCollectionScrollRestoration {
  key: string;
  read(): { x: number; y: number; rowKey?: string; rowOffset?: number } | undefined;
  write(position: { x: number; y: number; rowKey?: string; rowOffset?: number }): void;
}

export interface DesignCapabilityRendererContext extends RenderTreeNodeContext {
  transientFiles?: TransientFileRegistry;
  stream?: { sessionKey: string; status: string; model?: JsonObject; events: readonly JsonObject[]; errorCode?: string };
  sendStreamControl?(port: 'terminal.input' | 'terminal.resize' | 'stream.disconnect', payload: JsonObject): unknown;
  collectionScrollRestoration?: DesignCollectionScrollRestoration;
  entry: DesignCapabilityCatalogEntry;
  binding: unknown;
  model: JsonObject;
  translate: CapabilityTranslate;
  access: CapabilityAccessDecision;
  errorCode?: string;
  /** A retained model is visible while its current query is unavailable. */
  readOnly?: boolean;
  loading: boolean;
  empty: boolean;
  retry(): void;
  emitIntent(type: string, payload?: JsonObject, options?: { awaitCompletion?: boolean }): void | Promise<void>;
  pendingActionInvocations?: readonly DesignCapabilityPendingActionInvocation[];
  resolveMedia?(
    reference: GalleryCapabilityMediaReferenceModel,
  ): ResolvedGalleryCapabilityMedia | undefined;
}

export type DesignCapabilityRenderer = (
  context: DesignCapabilityRendererContext,
) => ReactNode;

export type DesignCapabilityRendererRegistry = Readonly<
  Record<string, DesignCapabilityRenderer>
>;

export interface CompiledDesignCapabilityRuntime {
  catalog: CompiledDesignCapabilityCatalog;
  providers: DesignCapabilityProviderRegistry;
  renderers: DesignCapabilityRendererRegistry;
  resolve(
    node: RenderNode,
    product: ProductContext,
  ): {
    entry: DesignCapabilityCatalogEntry;
    provider: DesignCapabilityProvider;
    renderer: DesignCapabilityRenderer;
  };
}

export type CapabilityBindingRegistry = Readonly<Record<string, unknown>>;

export type CapabilityAccessRegistry = Readonly<
  Record<string, CapabilityAccessDecision>
>;

export interface CapabilityRuntimeTreeContext {
  tree: RenderTree;
  bindings: CapabilityBindingRegistry;
  access: CapabilityAccessRegistry;
}

export const isJsonObject = (value: JsonValue | unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);
